import React, { useCallback, useEffect, useMemo } from "react"
import { withErrorBoundary } from "react-error-boundary"
import type { PurchaseType } from "components/Shop/PDPPage/types"
import { useLocale } from "hooks"
import { getMembershipWithId } from "lib/api"
import { Logger } from "lib/logger"
import type {
  MembershipOptionsV2,
  PricingToggle,
  ShippingType,
  VariantType,
  Price,
} from "types"

import { useVariantIds } from "hooks/useVariantIds"
import { useUser } from "lib/user"

import { useUSJourney } from "packages/acquisition/hooks/useUSJourney"

import { isProductOnlyPricingOption } from "./utils"

import { OpenGatePricing as OpenGatePricingPresentational } from "./OpenGatePricing"
import { onAddToBag } from "./onAddToBag"
import { PAGE_ELEMENT_CLICKED_EVENT_KEY } from "tracking/hooks/usePageElementEvents"
import { dispatch } from "lib/events"

type Props = {
  variant: VariantType
  setConfirmation: React.Dispatch<React.SetStateAction<boolean>>
  purchaseType: PurchaseType
  setPurchaseType: React.Dispatch<React.SetStateAction<PurchaseType>>
  shippingInfo?: ShippingType[]
  pricingToggle: PricingToggle
  pdpMembershipOptions?: MembershipOptionsV2[]
  membershipInBag?: boolean
}

export const convertV2MembershipToBasketItem = (sku: string) => {
  if (typeof sku === "undefined") {
    return
  }
  return getMembershipWithId(sku)
}

const PricingComponent: React.FC<Props> = ({
  variant,
  setConfirmation,
  setPurchaseType,
  shippingInfo,
  pricingToggle,
  pdpMembershipOptions,
  membershipInBag,
}) => {
  const { hasMembership } = useUser()
  const locale = useLocale()
  const { giftMembershipProductId } = useVariantIds()
  const isGiftMembership = variant.id === giftMembershipProductId

  const defaultPrice = pricingToggle?.data.prices.find(
    (priceOption) => priceOption.id === pricingToggle?.data.defaultPrice
  )

  const defaultMembershipSku =
    pdpMembershipOptions?.find((membershipOption) => membershipOption.isDefault)
      ?.sku ?? pdpMembershipOptions?.[0]?.sku

  const isUSJourney = useUSJourney()

  const onATB = useMemo(
    () =>
      onAddToBag({
        variant,
        isGiftMembership,
        locale,
        setPurchaseType,
        setConfirmation,
        hasMembership,
      }),
    [
      variant,
      isGiftMembership,
      locale,
      setPurchaseType,
      setConfirmation,
      hasMembership,
    ]
  )

  useEffect(() => {
    if (isUSJourney) {
      setPurchaseType("non-member")
    }
  }, [isUSJourney, setPurchaseType])

  const onAccordionChange = useCallback(
    (price: Price) => {
      const selectedPricingOption = pricingToggle?.data.prices.find(
        (priceOption) => priceOption.id === price.id
      )

      const eventProperties = {
        target: "og_pdp_panel",
        target2: defaultMembershipSku,
        location: "og_pdp_panel",
        locationDetails: defaultMembershipSku,
        details: isProductOnlyPricingOption(selectedPricingOption?.price)
          ? "og_radio_non_member"
          : "og_radio_member",
      }
      dispatch(PAGE_ELEMENT_CLICKED_EVENT_KEY, eventProperties)
    },
    [defaultMembershipSku, pricingToggle?.data.prices]
  )

  return (
    <OpenGatePricingPresentational
      {...{
        variant,
        shippingInfo,
        membershipInBag: !!membershipInBag,
        hasMembership,
        isGiftMembership,
        prices: pricingToggle?.data.prices,
        cardType: pricingToggle.cardType || "radio",
        onPricingSelection: onAccordionChange,
        onAddToBag: onATB,
        defaultPriceId: defaultPrice?.id || pricingToggle?.data.prices[0].id,
        defaultMembershipSku,
        pdpMembershipOptions,
      }}
    />
  )
}

export const OpenGatePricing = withErrorBoundary(PricingComponent, {
  fallback: <div />,
  onError(error) {
    Logger.warn(`Pricing failed to load. ${error}`)
  },
})
